import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import type { ChapterIntroDef } from "@/components/ChapterIntroBanner";
import {
    getScenarioBookends,
    type ScenarioBookendDef as ScenarioBookendSourceDef,
} from "@/content/scenarioChapters";

/**
 * 장 종료 시 풀스크린 컷신 배너. ChapterIntroBanner 의 "시작" 짝인 "끝".
 *
 * <p>2026-04-23 E20: 각 장의 <strong>마지막 주차</strong>에 도달하면 한 번 풀스크린
 * 으로 "이 장을 이렇게 끝냈다" + "다음 장 예고" 를 박아 준다. ChapterIntroBanner
 * 는 시작 주차에 짧게 뜨고 사라지는 배너, 여기는 끝에 길게(3.6s) 잡고 가는 컷.
 *
 * <p>sessionStorage key `chapter-bookend-seen:${runId}:${chapter.index}` 로 같은 런의
 * 같은 장에서 한 번만.
 *
 * <p>현재 frontier-vanguard 4 장 기준 플레이스홀더 텍스트가 들어가 있다. 콘텐츠
 * 팀이 최종 문구를 주면 {@link FRONTIER_VANGUARD_BOOKENDS} 상수만 교체.
 */
export interface ChapterBookendDef {
    /** {@link ChapterIntroDef#index} 와 매치. */
    index: number;
    /** 장 마지막 주차 (closing week). */
    endWeek: number;
    /** 회고 줄. 1~3 문장. */
    closingLine: string;
    /** 다음 장 예고 줄 (선택). 마지막 장은 null. */
    nextTeaser?: string | null;
    /** 장 자체 메타 (톤 힌트용). */
    chapter: ChapterIntroDef;
}

interface ChapterBookendProps {
    runId: string;
    currentWeek: number;
    bookends: ChapterBookendDef[];
}

export function ChapterBookend({ runId, currentWeek, bookends }: ChapterBookendProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    useEffect(() => {
        const hit = bookends.find(b => b.endWeek === currentWeek);
        if (!hit) return;
        const key = `chapter-bookend-seen:${runId}:${hit.index}`;
        if (typeof window !== "undefined" && window.sessionStorage?.getItem(key)) return;
        setActiveIndex(hit.index);
        if (typeof window !== "undefined") {
            window.sessionStorage?.setItem(key, "1");
        }
    }, [runId, currentWeek, bookends]);

    const active = activeIndex != null ? bookends.find(b => b.index === activeIndex) : null;
    if (!active) return null;
    const accent = toneAccent(active.chapter.tone);

    return (
        <AnimatePresence>
            <motion.div
                key={`bookend-${active.index}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="fixed inset-0 flex items-center justify-center cursor-pointer"
                style={{
                    zIndex: tokens.elevation.modal + 2,
                    background: `radial-gradient(circle at 50% 44%, ${accent}22 0%, ${tokens.color.ink}EE 38%, ${tokens.color.ink}F8 100%)`,
                    backdropFilter: "blur(8px)",
                }}
                onClick={() => setActiveIndex(null)}
                title="아무 곳이나 누르면 다음 장으로 이어집니다"
                role="dialog"
                aria-label={`${active.chapter.title} 장 종료 컷신`}
            >
                <div className="relative text-center pointer-events-auto" style={{ maxWidth: 760, padding: "0 48px" }}>
                    <motion.div
                        initial={{ opacity: 0, letterSpacing: "0.6em" }}
                        animate={{ opacity: 0.85, letterSpacing: "0.24em" }}
                        transition={{ delay: 0.15, duration: 0.7, ease: tokens.easing.decelerate }}
                        className="font-display"
                        style={{
                            color: accent,
                            fontSize: tokens.type.size.small,
                            textTransform: "uppercase",
                        }}
                    >
                        {active.chapter.startWeek}–{active.endWeek}주 · 장의 끝
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.7, ease: tokens.easing.decelerate }}
                        className="mt-s font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: "clamp(28px, 3.2vw, 44px)",
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `0 0 28px ${accent}88, 0 2px 12px ${tokens.color.ink}`,
                        }}
                    >
                        {active.chapter.title}을 덮는다
                    </motion.div>
                    <motion.div
                        aria-hidden
                        initial={{ width: "15%", opacity: 0 }}
                        animate={{ width: "48%", opacity: 0.7 }}
                        transition={{ delay: 0.55, duration: 0.6 }}
                        className="mx-auto mt-m"
                        style={{
                            height: 1,
                            background: `linear-gradient(90deg, transparent, ${accent}AA, transparent)`,
                            boxShadow: `0 0 18px ${accent}66`,
                        }}
                    />
                    <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 0.96, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.7 }}
                        className="mt-m font-body"
                        style={{
                            color: tokens.color.parchmentDim,
                            fontSize: tokens.type.size.body,
                            lineHeight: tokens.type.lineHeight.loose,
                            fontStyle: "italic",
                            textShadow: `0 1px 6px ${tokens.color.ink}`,
                        }}
                    >
                        {active.closingLine}
                    </motion.p>
                    {active.nextTeaser && (
                        <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.4, duration: 0.6, ease: tokens.easing.decelerate }}
                            className="mt-l rounded-m px-m py-s inline-flex items-center gap-s"
                            style={{
                                background: `linear-gradient(180deg, ${tokens.color.ink}D4 0%, ${tokens.color.ink}B8 100%)`,
                                border: `1px solid ${accent}55`,
                                boxShadow: `inset 0 1px 0 ${accent}22, 0 0 14px ${accent}22`,
                            }}
                        >
                            {/* 2026-04-23: "다음 장 예고" 폴리시 — uppercase kicker + accent dash + 강화된 border. */}
                            <span
                                aria-hidden
                                style={{
                                    width: 14,
                                    height: 1,
                                    background: accent,
                                    boxShadow: `0 0 6px ${accent}`,
                                    opacity: 0.7,
                                }}
                            />
                            <span
                                className="font-body"
                                style={{
                                    color: accent,
                                    fontSize: 10,
                                    letterSpacing: "0.22em",
                                    textTransform: "uppercase",
                                }}
                            >
                                다음 장
                            </span>
                            <span
                                className="font-body"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.small,
                                    letterSpacing: "0.02em",
                                }}
                            >
                                {active.nextTeaser}
                            </span>
                        </motion.div>
                    )}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        transition={{ delay: 1.8, duration: 0.5 }}
                        className="mt-l font-body"
                        style={{
                            color: tokens.color.parchmentDim,
                            fontSize: tokens.type.size.tiny,
                            letterSpacing: tokens.type.letterSpacing.wide,
                        }}
                    >
                        아무 곳이나 누르면 다음 장으로 이어집니다
                    </motion.div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

function toneAccent(tone: ChapterIntroDef["tone"]): string {
    switch (tone) {
        case "steel": return tokens.color.steel;
        case "brass": return tokens.color.brass;
        case "ember": return tokens.color.ember;
        case "burgundy": return tokens.color.burgundy;
        default: return tokens.color.brass;
    }
}

export const FRONTIER_VANGUARD_BOOKENDS: ScenarioBookendSourceDef[] = getScenarioBookends("frontier-vanguard");
